import Restore from "@mui/icons-material/Restore";
import { Box, Chip, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ScoutChat, type ScoutConversationSettingsState } from "./Chat";
import { isScoutOptimisticConversationId } from "./hooks/scoutConversationIds";
import { SCOUT_WRITE_MODE_GQL } from "./hooks/scoutWriteMode";
import { useScoutPopoverSession } from "./PopoverScout/ScoutPopoverSessionContext";
import { SCOUT_ACCESS_PACKAGES, useScoutAccessPackage } from "./scoutAccess";
import { useScoutInfoPanel } from "./ScoutInfoPanel";
import { SCOUT_FULL_PAGE_HEADER_HEIGHT_PX, SCOUT_SCROLLBAR_GUTTER_PX } from "./scoutLayout";
import { ScoutConversationHeader, ScoutSidePanel } from "./ScoutToolbar";

type ConversationTitleState = {
  conversationId: string | undefined;
  title: string | undefined;
};

function resolveDisplayedConversationTitle(input: {
  conversationTitle: ConversationTitleState | undefined;
  conversationId: string | undefined;
  hasMessages: boolean;
}) {
  if (!input.conversationTitle) {
    return undefined;
  }

  if (input.conversationTitle.conversationId === input.conversationId) {
    return input.conversationTitle.title;
  }

  if (!input.conversationId && input.hasMessages) {
    return input.conversationTitle.title;
  }

  return undefined;
}

export function Scout() {
  const theme = useTheme();
  const backgroundColor = theme.palette.scoutCanvas;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const height = isMobile ? xsHeight : mdHeight;

  const { tenantId, conversationId } = useParams();
  const navigate = useNavigate();
  const popoverSession = useScoutPopoverSession();
  const { scoutAccessPackage } = useScoutAccessPackage();
  const hasScoutProAccess = scoutAccessPackage === SCOUT_ACCESS_PACKAGES.PRO;
  const [hasMessages, setHasMessages] = useState(false);
  const [conversationTitle, setConversationTitle] = useState<ConversationTitleState | undefined>();
  const [conversationSettings, setConversationSettings] = useState<ScoutConversationSettingsState>({
    writeMode: SCOUT_WRITE_MODE_GQL.OFF,
    onWriteModeChange: () => undefined,
    settingsDisabled: false,
  });
  const handleHasMessagesChange = useCallback((nextHasMessages: boolean) => {
    setHasMessages(nextHasMessages);
  }, []);
  const handleConversationTitleChange = useCallback((nextTitle: ConversationTitleState) => {
    setConversationTitle(nextTitle);
  }, []);

  let scoutTierEndAdornment: "scout-tier-pro" | "scout-tier-regulatory-advisor" | undefined;
  if (scoutAccessPackage === SCOUT_ACCESS_PACKAGES.PRO) {
    scoutTierEndAdornment = "scout-tier-pro";
  } else if (scoutAccessPackage === SCOUT_ACCESS_PACKAGES.REGULATORY_ADVISOR) {
    scoutTierEndAdornment = "scout-tier-regulatory-advisor";
  }

  useBreadcrumb({
    label: "Encamp Scout",
    startIcon: "scout",
    endAdornment: scoutTierEndAdornment,
  });
  const displayedConversationTitle = resolveDisplayedConversationTitle({
    conversationTitle,
    conversationId,
    hasMessages,
  });
  const {
    open: isSidePanelOpen,
    view: sidePanelView,
    tab: sidePanelTab,
    changeTab: changeSidePanelTab,
    isPersistentMode: isPersistentSidePanelMode,
    openToView,
    togglePanel,
    backFromSearch,
    closePanel: closeSidePanel,
  } = useScoutInfoPanel();

  // Search is embedded in the side panel across all breakpoints.
  const handleOpenSearch = useCallback(() => {
    openToView("search");
  }, [openToView]);

  useEffect(() => {
    setHasMessages(false);
  }, [conversationId]);

  // Landing on the full Scout page makes the top-nav popover redundant — close
  // it (keeping the conversation pointer so we can offer to resume it here).
  const { close: closePopover, reset: resetPopover } = popoverSession;
  useEffect(() => {
    closePopover();
  }, [closePopover]);

  // The empty state (NewChat) has no scrolling content to mask, and its
  // vertically-centered hero logo can drift up into the header band on short /
  // zoomed-in viewports. Keeping the overlay opaque there clips the logo, so
  // render it transparent until there's real conversation content beneath it.
  const isEmptyConversation = !conversationId && !hasMessages;

  const popoverConversationId = popoverSession.conversationId;
  const canResumePopoverConversation =
    Boolean(popoverConversationId) &&
    !isScoutOptimisticConversationId(popoverConversationId) &&
    popoverConversationId !== conversationId;

  const handleResumePopoverConversation = useCallback(() => {
    if (!tenantId || !popoverConversationId) {
      return;
    }
    navigate(`/o/${tenantId}/scout/${popoverConversationId}`);
    // The conversation is now owned by the full page (via the URL) — clear the
    // popover session so the chip disappears.
    resetPopover();
  }, [navigate, popoverConversationId, resetPopover, tenantId]);

  const resumeConversationChip = canResumePopoverConversation ? (
    <Tooltip title="Resume your Scout conversation here">
      <Chip
        icon={<Restore />}
        label={popoverSession.title ? `Resume "${popoverSession.title}"` : "Resume conversation"}
        onClick={handleResumePopoverConversation}
        size="small"
        variant="outlined"
        sx={{ maxWidth: 260 }}
      />
    </Tooltip>
  ) : null;

  return (
    <Stack
      sx={{
        position: "relative",
        width: "100%",
        height: `calc(100vh - ${theme.spacing(height)})`,
        overflow: "hidden",
        backgroundColor,
      }}
    >
      <Stack
        direction="row"
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ScoutToolbarOverlay
            backgroundColor={isEmptyConversation ? "transparent" : backgroundColor}
          >
            <ScoutConversationHeader
              conversationTitle={displayedConversationTitle}
              showTitle={Boolean(conversationId) || hasMessages}
              writeMode={conversationSettings.writeMode}
              onWriteModeChange={conversationSettings.onWriteModeChange}
              settingsDisabled={conversationSettings.settingsDisabled}
              hasScoutProAccess={hasScoutProAccess}
              endContent={resumeConversationChip}
            />
          </ScoutToolbarOverlay>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ScoutChat
              onConversationTitleChange={handleConversationTitleChange}
              onHasMessagesChange={handleHasMessagesChange}
              onConversationSettingsChange={setConversationSettings}
              hasScoutProAccess={hasScoutProAccess}
            />
          </Box>
        </Box>

        <ScoutSidePanel
          open={isSidePanelOpen}
          view={sidePanelView}
          tab={sidePanelTab}
          isPersistentMode={isPersistentSidePanelMode}
          onTogglePanel={togglePanel}
          onOpenView={openToView}
          onOpenSearch={handleOpenSearch}
          onClose={closeSidePanel}
          onTabChange={changeSidePanelTab}
          onBackFromSearch={backFromSearch}
        />
      </Stack>
    </Stack>
  );
}

function ScoutToolbarOverlay({
  backgroundColor,
  children,
}: {
  backgroundColor: string;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        height: `${SCOUT_FULL_PAGE_HEADER_HEIGHT_PX}px`,
        position: "absolute",
        top: 0,
        left: 0,
        // Stop short of the scrollbar so it stays visible all the way to the top.
        right: `${SCOUT_SCROLLBAR_GUTTER_PX}px`,
        zIndex: 2,
        backgroundColor,
        pointerEvents: "none",
      }}
    >
      {children}
    </Box>
  );
}
